import { useEffect } from 'react'
import { Loader2, Package } from 'lucide-react'
import { PageLayout } from '@/layouts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Product } from '../../state/selectProduct'
import { useFetchApuracaoByIdProduct } from '../../services/useFetchApuracaoByIdProduct'
import { useApuracaoSelecionada } from '../../hooks/seApuracaoSelecionada'
import { useFetchMvaDashboard } from '../../services/useFetchMvaDashboard'
import { useSaveMvaGtins } from '../../services/useSaveMvaGtins'
import { ProductQuantityChecker, type GTINProduct } from '../GTIN/product-quantity-checker'
import { formatCurrency } from '../../utils'

type MvaProps = {
  cnpj?: string
  product: Product
}

export default function MVA({ product }: MvaProps) {
  const { data: apuracoes = [], isLoading: isLoadingApuracoes } = useFetchApuracaoByIdProduct(product?.id ?? '')
  const { apuracaoSelecionada, setApuracaoSelecionada } = useApuracaoSelecionada()

  useEffect(() => {
    if (apuracoes.length > 0 && !apuracaoSelecionada) {
      setApuracaoSelecionada(apuracoes[0])
    }
  }, [apuracoes, apuracaoSelecionada, setApuracaoSelecionada])

  const idApuracao = apuracaoSelecionada?.id ?? ''
  const { data: dashboard, isLoading: isLoadingDashboard } = useFetchMvaDashboard(idApuracao)
  const saveGtins = useSaveMvaGtins(idApuracao)

  const gtinProducts: GTINProduct[] =
    dashboard?.gtins.map((item, index) => ({
      id: `${item.cEAN ?? 'sem-ean'}-${item.ncm ?? 'sem-ncm'}-${index}`,
      cEAN: item.cEAN ?? 'SEM EAN',
      xProd: item.xProd ?? 'Produto sem descrição',
      uCom: item.uCom ?? 'UN',
      ncm: item.ncm ?? '',
      cest: item.cest,
      status: item.isConfigurado ? 'CONFIGURADO' : 'PENDENTE',
      qntd: item.quantidadeEmbalagem
    })) ?? []

  const handleSaveAll = async (items: GTINProduct[]) => {
    await saveGtins.mutateAsync({
      items: items.map(item => ({
        cEAN: item.cEAN,
        xProd: item.xProd,
        uCom: item.uCom,
        ncm: item.ncm,
        cest: item.cest,
        quantidadeEmbalagem: item.qntd
      }))
    })
  }

  if (isLoadingApuracoes || isLoadingDashboard) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    )
  }

  if (!apuracaoSelecionada) {
    return <p>Nenhuma apuração encontrada.</p>
  }

  return (
    <PageLayout title="MVA" showSidebarTrigger={false}>
      <div className="space-y-6">
        <Card className="border-none shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary">
                <Package className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <CardTitle>Fluxo MVA por Competência</CardTitle>
                <CardDescription>
                  Configure o fator GTIN para converter entrada e saída em quantidade real e fechar o mês com base nos
                  XMLs já importados.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Apuração selecionada</p>
              <p className="font-semibold">{apuracaoSelecionada.name || apuracaoSelecionada.id}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Competência vinculada</p>
              <p className="font-semibold">{dashboard?.id_apuracao_competencia || 'Não vinculada'}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Produtos no fluxo</p>
              <p className="font-semibold">{dashboard?.gtins.length ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {(dashboard?.fechamentosMensais ?? []).map(item => (
            <Card key={item.competencia}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{item.competencia}</CardTitle>
                <CardDescription>{item.totalProdutosSemGtin} item(ns) ainda sem fator GTIN</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Entradas</span>
                  <span>{formatCurrency(item.totalEntradasValor)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Saídas</span>
                  <span>{formatCurrency(item.totalSaidasValor)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">ICMS-ST cobrado</span>
                  <span>{formatCurrency(item.totalIcmsStCobrado)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Base ST retida</span>
                  <span>{formatCurrency(item.totalBaseSTRetida)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Qtd. entrada real</span>
                  <span>{item.totalEntradasQuantidadeReal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Qtd. saída real</span>
                  <span>{item.totalSaidasQuantidadeReal.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <ProductQuantityChecker
          products={gtinProducts}
          isSaving={saveGtins.isPending}
          onSaveAll={handleSaveAll}
          title="GTIN e Quantidade por Embalagem"
          description="Informe quantas unidades reais existem em cada referência fiscal para converter compras e vendas."
        />
      </div>
    </PageLayout>
  )
}
